import { useState, useEffect } from 'react';

interface RateLimitMessageProps {
  /** Time in milliseconds until rate limit resets */
  resetIn: number;
  /** Callback when user wants to enter plant name manually */
  onEnterManually: () => void;
}

/**
 * Displays a rate limit message with a countdown timer.
 * Shows remaining time until the user can try identification again.
 */
export function RateLimitMessage({ resetIn, onEnterManually }: RateLimitMessageProps) {
  const [timeLeft, setTimeLeft] = useState(Math.ceil(resetIn / 1000));

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time remaining
  const formatTime = (seconds: number): string => {
    if (seconds <= 0) return 'now';
    if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const progress = Math.max(0, timeLeft / Math.ceil(resetIn / 1000));

  return (
    <div
      className="rounded-lg bg-danger/10 p-6"
      role="alert"
      aria-live="polite"
    >
      <div className="flex flex-col items-center text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger/20">
          <span className="material-symbols-outlined text-danger" style={{ fontSize: 32 }} aria-hidden="true">
            schedule
          </span>
        </div>

        <h3 className="text-lg font-display font-bold text-editorial-black">
          Too many requests
        </h3>

        <p className="mt-2 text-sm font-serif text-gray-500">
          You've reached the identification limit. Try again in{' '}
          <span className="font-semibold text-danger">{formatTime(timeLeft)}</span>.
        </p>

        {/* Progress bar */}
        <div className="mt-4 h-2 w-full rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-danger transition-all duration-1000"
            style={{ width: `${progress * 100}%` }}
            role="progressbar"
            aria-valuenow={timeLeft}
            aria-valuemax={Math.ceil(resetIn / 1000)}
            aria-label="Time remaining"
          />
        </div>

        {/* Manual entry option */}
        <button
          onClick={onEnterManually}
          className="mt-6 flex h-btn-lg min-h-btn-lg w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-6 font-sans font-bold text-xs uppercase tracking-widest text-editorial-black transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
          aria-label="Enter plant name manually"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">
            edit
          </span>
          Enter plant name manually
        </button>

        <p className="mt-4 text-xs font-sans text-gray-400">
          Tip: You can still add plants by entering the name manually while waiting.
        </p>
      </div>
    </div>
  );
}
